import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ModalComponent } from 'src/app/_components/modals/modal.component';
import {
  CalibrationResults,
  CalibrationUpdate,
} from 'src/app/_models/calibration';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { Laboratory } from 'src/app/_models/laboratory';
import {
  OxygenCalibration,
  OxygenCalibrationUpdate,
} from 'src/app/_models/oxygen-calibration';
import {
  PhCalibration,
  PhCalibrationUpdate,
} from 'src/app/_models/ph-calibration';
import {
  SpargingCalibration,
  SpargingCalibrationUpdate,
} from 'src/app/_models/sparging-calibration';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { ModalService } from 'src/app/_services/modal.service';
import { advancedNameRegex } from 'src/app/_helpers/utils';
import { NotesParameterRanges } from 'src/app/_helpers/notes-min-max';
import { CalibrationParameterRanges } from 'src/app/_helpers/calibration-min-max';
import { TabOptions } from 'src/app/_models/tab-options';
import { UnderlinedFormFieldComponent } from 'src/app/_components/forms/underlined-form-field/underlined-form-field.component';
import { UnderlinedTextareaFieldComponent } from 'src/app/_components/forms/underlined-textarea-field/underlined-textarea-field.component';
import { AltOptionTabToggleComponent } from 'src/app/_components/selection-controls/alt-option-tab-toggle/alt-option-tab-toggle.component';
import { Module } from 'src/app/_models/module-enum';
import { NumberFormFieldComponent } from 'src/app/_components/forms/number-form-field/number-form-field.component';
import { CalibrationService } from 'src/app/_services/calibration.service';
import { ButtonComponent } from 'src/app/_components/buttons/button/button.component';
import { User } from 'src/app/_models/user';
import { EDIT_ACCESS_ROLE_LIST, Role } from 'src/app/_models/role';
import { RoleCheckPipe } from '../../_helpers/role-check.pipe';
import { ErrorPageComponent } from '../../_components/error-page/error-page.component';

@Component({
  selector: 'app-edit-calibration',
  imports: [
    ModalComponent,
    UnderlinedFormFieldComponent,
    UnderlinedTextareaFieldComponent,
    AltOptionTabToggleComponent,
    NumberFormFieldComponent,
    ButtonComponent,
    RoleCheckPipe,
    ErrorPageComponent,
  ],
  templateUrl: './edit-calibration.component.html',
  styleUrl: './edit-calibration.component.scss',
})
export class EditCalibrationComponent implements OnInit {
  protected readonly formBuilder = inject(FormBuilder);
  protected readonly route = inject(ActivatedRoute);
  protected readonly authenticationService = inject(AuthenticationService);
  protected modalService = inject(ModalService);
  protected calibrationService = inject(CalibrationService);
  protected router = inject(Router);

  ModuleEnum = Module;
  Role = Role;
  readonly editAccessRoles = EDIT_ACCESS_ROLE_LIST;

  allowNavigation: Subject<boolean> = new Subject<boolean>();
  private readonly unsubscribe$ = new Subject<void>();

  error: ErrorObject = null;
  loadingError = false;
  isProcessing = false;

  currentUser: User;

  navOptions: TabOptions[];
  selectedTabOption = '100';

  displayForm = false;

  calibrationId: number;
  calibrationType: string;
  calibrationModuleType: Module;
  laboratory: Laboratory;

  odCalibration: CalibrationResults;
  odCalibrationUpdate: CalibrationUpdate;
  oxygenCalibration: OxygenCalibration;
  oxygenCalibrationUpdate: OxygenCalibrationUpdate;
  phCalibration: PhCalibration;
  phCalibrationUpdate: PhCalibrationUpdate;
  spargingCalibration: SpargingCalibration;
  spargingCalibrationUpdate: SpargingCalibrationUpdate;

  notesMinLength = NotesParameterRanges.CALIBRATION_NOTES.minNoteLength;
  notesMaxLength = NotesParameterRanges.CALIBRATION_NOTES.maxNoteLength;

  // Calibration parameters
  nameLengthMin = CalibrationParameterRanges.NAME.min;
  nameLengthMax = CalibrationParameterRanges.NAME.max;
  odOffsetMin = CalibrationParameterRanges.OD_OFFSET.min;
  odOffsetMax = CalibrationParameterRanges.OD_OFFSET.max;
  odOffsetStep = CalibrationParameterRanges.OD_OFFSET.step;
  odBelowRangeWarning = CalibrationParameterRanges.OD_OFFSET.belowRangeWarning;
  odBelowRangeWarningMessage =
    CalibrationParameterRanges.OD_OFFSET.warningMessage;
  odFactorMin = CalibrationParameterRanges.OD_FACTOR.min;
  odFactorMax = CalibrationParameterRanges.OD_FACTOR.max;
  odFactorStep = CalibrationParameterRanges.OD_FACTOR.step;
  odPowerMin = CalibrationParameterRanges.OD_POWER.min;
  odPowerMax = CalibrationParameterRanges.OD_POWER.max;
  odPowerStep = CalibrationParameterRanges.OD_POWER.step;
  odPowerAboveRangeWarning =
    CalibrationParameterRanges.OD_POWER.aboveRangeWarning;
  odPowerAboveRangeWarningMessage =
    CalibrationParameterRanges.OD_POWER.warningMessage;
  odTemperatureMin = CalibrationParameterRanges.OD_TEMPERATURE.min;
  odTemperatureMax = CalibrationParameterRanges.OD_TEMPERATURE.max;
  odTemperatureStep = CalibrationParameterRanges.OD_TEMPERATURE.step;

  oxygenDphi0Min = CalibrationParameterRanges.OXYGEN_DPHI_0.min;
  oxygenDphi0Max = CalibrationParameterRanges.OXYGEN_DPHI_0.max;
  oxygenDphi0Step = CalibrationParameterRanges.OXYGEN_DPHI_0.step;
  oxygenDphi100Min = CalibrationParameterRanges.OXYGEN_DPHI_100.min;
  oxygenDphi100Max = CalibrationParameterRanges.OXYGEN_DPHI_100.max;
  oxygenDphi100Step = CalibrationParameterRanges.OXYGEN_DPHI_100.step;
  oxygenTemp0Min = CalibrationParameterRanges.OXYGEN_TEMPERATURE_0.min;
  oxygenTemp0Max = CalibrationParameterRanges.OXYGEN_TEMPERATURE_0.max;
  oxygenTemp0Step = CalibrationParameterRanges.OXYGEN_TEMPERATURE_0.step;
  oxygenTemp100Min = CalibrationParameterRanges.OXYGEN_TEMPERATURE_100.min;
  oxygenTemp100Max = CalibrationParameterRanges.OXYGEN_TEMPERATURE_100.max;
  oxygenTemp100Step = CalibrationParameterRanges.OXYGEN_TEMPERATURE_100.step;
  oxygenPressureMin = CalibrationParameterRanges.OXYGEN_PRESSURE.min;
  oxygenPressureMax = CalibrationParameterRanges.OXYGEN_PRESSURE.max;
  oxygenPressureStep = CalibrationParameterRanges.OXYGEN_PRESSURE.step;

  phSlopeMin = CalibrationParameterRanges.PH_SLOPE.min;
  phSlopeMax = CalibrationParameterRanges.PH_SLOPE.max;
  phSlopeStep = CalibrationParameterRanges.PH_SLOPE.step;
  phSlopeBelowRangeWarning =
    CalibrationParameterRanges.PH_SLOPE.belowRangeWarning;
  phSlopeAboveRangeWarning =
    CalibrationParameterRanges.PH_SLOPE.aboveRangeWarning;
  phSlopeWarningMessage = CalibrationParameterRanges.PH_SLOPE.warningMessage;
  phOffsetMin = CalibrationParameterRanges.PH_OFFSET.min;
  phOffsetMax = CalibrationParameterRanges.PH_OFFSET.max;
  phOffsetStep = CalibrationParameterRanges.PH_OFFSET.step;
  phBelowRangeWarning = CalibrationParameterRanges.PH_OFFSET.belowRangeWarning;
  phBelowRangeWarningMessage =
    CalibrationParameterRanges.PH_OFFSET.warningMessage;
  phTemperatureMin = CalibrationParameterRanges.PH_TEMPERATURE.min;
  phTemperatureMax = CalibrationParameterRanges.PH_TEMPERATURE.max;
  phTemperatureStep = CalibrationParameterRanges.PH_TEMPERATURE.step;
  spargingSlopeMin = CalibrationParameterRanges.SPARGING_SLOPE.min;
  spargingSlopeMax = CalibrationParameterRanges.SPARGING_SLOPE.max;
  spargingSlopeStep = CalibrationParameterRanges.SPARGING_SLOPE.step;
  spargingInterceptMin = CalibrationParameterRanges.SPARGING_INTERCEPT.min;
  spargingInterceptMax = CalibrationParameterRanges.SPARGING_INTERCEPT.max;
  spargingInterceptStep = CalibrationParameterRanges.SPARGING_INTERCEPT.step;
  r2Min = CalibrationParameterRanges.R2.min;
  r2Max = CalibrationParameterRanges.R2.max;
  r2Step = CalibrationParameterRanges.R2.step;

  calibrationNameField = new FormBase({
    key: 'calibrationName',
    label: 'Calibration Name',
    type: 'text',
    placeholder: 'Calibration Name',
    disabled: false,
    required: true,
    value: null,
    options: [],
  });

  noteField = new FormBase({
    key: 'noteField',
    label: 'Note',
    type: 'textarea',
    placeholder: 'Enter notes here...',
    disabled: false,
    required: false,
    value: '',
    options: [],
  });

  // OD calibration
  offsetAField = new FormBase<number>({
    key: 'offsetA',
    label: 'Offset (p0)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_OFFSET.default,
    options: [],
  });

  offsetBField = new FormBase<number>({
    key: 'offsetB',
    label: 'Offset (p0)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_OFFSET.default,
    options: [],
  });

  offsetCField = new FormBase<number>({
    key: 'offsetC',
    label: 'Offset (p0)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_OFFSET.default,
    options: [],
  });

  offsetDField = new FormBase<number>({
    key: 'offsetD',
    label: 'Offset (p0)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_OFFSET.default,
    options: [],
  });

  factorAField = new FormBase<number>({
    key: 'factorA',
    label: 'Factor (p1)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_FACTOR.default,
    options: [],
  });

  factorBField = new FormBase<number>({
    key: 'factorB',
    label: 'Factor (p1)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_FACTOR.default,
    options: [],
  });

  factorCField = new FormBase<number>({
    key: 'factorC',
    label: 'Factor (p1)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_FACTOR.default,
    options: [],
  });

  factorDField = new FormBase<number>({
    key: 'factorD',
    label: 'Factor (p1)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_FACTOR.default,
    options: [],
  });

  powerAField = new FormBase<number>({
    key: 'powerA',
    label: 'Power (p2)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_POWER.default,
    options: [],
  });

  powerBField = new FormBase<number>({
    key: 'powerB',
    label: 'Power (p2)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_POWER.default,
    options: [],
  });

  powerCField = new FormBase<number>({
    key: 'powerC',
    label: 'Power (p2)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_POWER.default,
    options: [],
  });

  powerDField = new FormBase<number>({
    key: 'powerD',
    label: 'Power (p2)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_POWER.default,
    options: [],
  });

  temperatureAField = new FormBase<number>({
    key: 'temperatureA',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_TEMPERATURE.default,
    options: [],
  });

  temperatureBField = new FormBase<number>({
    key: 'temperatureB',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_TEMPERATURE.default,
    options: [],
  });

  temperatureCField = new FormBase<number>({
    key: 'temperatureC',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_TEMPERATURE.default,
    options: [],
  });

  temperatureDField = new FormBase<number>({
    key: 'temperatureD',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_TEMPERATURE.default,
    options: [],
  });

  // Oxygen calibration
  temperature100AField = new FormBase<number>({
    key: 'temperature100A',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_100.default,
    options: [],
  });

  temperature100BField = new FormBase<number>({
    key: 'temperature100B',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_100.default,
    options: [],
  });

  temperature100CField = new FormBase<number>({
    key: 'temperature100C',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_100.default,
    options: [],
  });

  temperature100DField = new FormBase<number>({
    key: 'temperature100D',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_100.default,
    options: [],
  });

  temperature0AField = new FormBase<number>({
    key: 'temperature0A',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_0.default,
    options: [],
  });

  temperature0BField = new FormBase<number>({
    key: 'temperature0B',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_0.default,
    options: [],
  });

  temperature0CField = new FormBase<number>({
    key: 'temperature0C',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_0.default,
    options: [],
  });

  temperature0DField = new FormBase<number>({
    key: 'temperature0D',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_0.default,
    options: [],
  });

  dphi100AField = new FormBase<number>({
    key: 'dphi100A',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_100.default,
    options: [],
  });

  dphi100BField = new FormBase<number>({
    key: 'dphi100B',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_100.default,
    options: [],
  });

  dphi100CField = new FormBase<number>({
    key: 'dphi100C',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_100.default,
    options: [],
  });

  dphi100DField = new FormBase<number>({
    key: 'dphi100D',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_100.default,
    options: [],
  });

  dphi0AField = new FormBase<number>({
    key: 'dphi0A',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_0.default,
    options: [],
  });

  dphi0BField = new FormBase<number>({
    key: 'dphi0B',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_0.default,
    options: [],
  });

  dphi0CField = new FormBase<number>({
    key: 'dphi0C',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_0.default,
    options: [],
  });

  dphi0DField = new FormBase<number>({
    key: 'dphi0D',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_0.default,
    options: [],
  });

  pressure100AField = new FormBase<number>({
    key: 'pressure100A',
    label: 'Pressure',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_PRESSURE.default,
    options: [],
  });

  pressure100BField = new FormBase<number>({
    key: 'pressure100B',
    label: 'Pressure',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_PRESSURE.default,
    options: [],
  });

  pressure100CField = new FormBase<number>({
    key: 'pressure100C',
    label: 'Pressure',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_PRESSURE.default,
    options: [],
  });

  pressure100DField = new FormBase<number>({
    key: 'pressure100D',
    label: 'Pressure',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_PRESSURE.default,
    options: [],
  });

  // pH Fields
  phSlopeAField = new FormBase<number>({
    key: 'slopeA',
    label: 'Slope [%]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_SLOPE.default,
    options: [],
  });

  phSlopeBField = new FormBase<number>({
    key: 'slopeB',
    label: 'Slope [%]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_SLOPE.default,
    options: [],
  });

  phSlopeCField = new FormBase<number>({
    key: 'slopeC',
    label: 'Slope [%]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_SLOPE.default,
    options: [],
  });

  phSlopeDField = new FormBase<number>({
    key: 'slopeD',
    label: 'Slope [%]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_SLOPE.default,
    options: [],
  });

  phOffsetAField = new FormBase<number>({
    key: 'offsetA',
    label: 'Offset [mV]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_OFFSET.default,
    options: [],
  });

  phOffsetBField = new FormBase<number>({
    key: 'offsetB',
    label: 'Offset [mV]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_OFFSET.default,
    options: [],
  });

  phOffsetCField = new FormBase<number>({
    key: 'offsetC',
    label: 'Offset [mV]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_OFFSET.default,
    options: [],
  });

  phOffsetDField = new FormBase<number>({
    key: 'offsetD',
    label: 'Offset [mV]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_OFFSET.default,
    options: [],
  });

  // Sparging fields
  spargingSlopeAField = new FormBase<number>({
    key: 'slopeA',
    label: 'Slope [%]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_SLOPE.default,
    options: [],
  });

  spargingSlopeBField = new FormBase<number>({
    key: 'slopeB',
    label: 'Slope [%]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_SLOPE.default,
    options: [],
  });

  spargingSlopeCField = new FormBase<number>({
    key: 'slopeC',
    label: 'Slope [%]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_SLOPE.default,
    options: [],
  });

  spargingSlopeDField = new FormBase<number>({
    key: 'slopeD',
    label: 'Slope [%]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_SLOPE.default,
    options: [],
  });

  interceptAField = new FormBase<number>({
    key: 'interceptA',
    label: 'Intercept',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_INTERCEPT.default,
    options: [],
  });

  interceptBField = new FormBase<number>({
    key: 'interceptB',
    label: 'Intercept',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_INTERCEPT.default,
    options: [],
  });

  interceptCField = new FormBase<number>({
    key: 'interceptC',
    label: 'Intercept',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_INTERCEPT.default,
    options: [],
  });

  interceptDField = new FormBase<number>({
    key: 'interceptD',
    label: 'Intercept',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_INTERCEPT.default,
    options: [],
  });

  // R2 fields
  r2AField = new FormBase<number>({
    key: 'r2A',
    label: 'R2',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.R2.default,
    options: [],
  });

  r2BField = new FormBase<number>({
    key: 'r2B',
    label: 'R2',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.R2.default,
    options: [],
  });

  r2CField = new FormBase<number>({
    key: 'r2C',
    label: 'R2',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.R2.default,
    options: [],
  });

  r2DField = new FormBase<number>({
    key: 'r2D',
    label: 'R2',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.R2.default,
    options: [],
  });

  editCalibrationForm = this.formBuilder.group({
    [this.calibrationNameField.key]: [
      this.calibrationNameField.value,
      [
        Validators.minLength(this.nameLengthMin),
        Validators.maxLength(this.nameLengthMax),
        Validators.required,
        Validators.pattern(advancedNameRegex),
      ],
    ],
    [this.noteField.key]: [
      '',
      [
        Validators.minLength(this.notesMinLength),
        Validators.maxLength(this.notesMaxLength),
      ],
    ],
  });

  // OD calibration form
  odCalibrationForm = this.formBuilder.group({
    [this.offsetAField.key]: [
      this.offsetAField.value,
      [
        Validators.required,
        Validators.min(this.odOffsetMin),
        Validators.max(this.odOffsetMax),
      ],
    ],
    [this.offsetBField.key]: [
      this.offsetBField.value,
      [
        Validators.required,
        Validators.min(this.odOffsetMin),
        Validators.max(this.odOffsetMax),
      ],
    ],
    [this.offsetCField.key]: [
      this.offsetCField.value,
      [
        Validators.required,
        Validators.min(this.odOffsetMin),
        Validators.max(this.odOffsetMax),
      ],
    ],
    [this.offsetDField.key]: [
      this.offsetDField.value,
      [
        Validators.required,
        Validators.min(this.odOffsetMin),
        Validators.max(this.odOffsetMax),
      ],
    ],
    [this.factorAField.key]: [
      this.factorAField.value,
      [
        Validators.required,
        Validators.min(this.odFactorMin),
        Validators.max(this.odFactorMax),
      ],
    ],
    [this.factorBField.key]: [
      this.factorBField.value,
      [
        Validators.required,
        Validators.min(this.odFactorMin),
        Validators.max(this.odFactorMax),
      ],
    ],
    [this.factorCField.key]: [
      this.factorCField.value,
      [
        Validators.required,
        Validators.min(this.odFactorMin),
        Validators.max(this.odFactorMax),
      ],
    ],
    [this.factorDField.key]: [
      this.factorDField.value,
      [
        Validators.required,
        Validators.min(this.odFactorMin),
        Validators.max(this.odFactorMax),
      ],
    ],
    [this.powerAField.key]: [
      this.powerAField.value,
      [
        Validators.required,
        Validators.min(this.odPowerMin),
        Validators.max(this.odPowerMax),
      ],
    ],
    [this.powerBField.key]: [
      this.powerBField.value,
      [
        Validators.required,
        Validators.min(this.odPowerMin),
        Validators.max(this.odPowerMax),
      ],
    ],
    [this.powerCField.key]: [
      this.powerCField.value,
      [
        Validators.required,
        Validators.min(this.odPowerMin),
        Validators.max(this.odPowerMax),
      ],
    ],
    [this.powerDField.key]: [
      this.powerDField.value,
      [
        Validators.required,
        Validators.min(this.odPowerMin),
        Validators.max(this.odPowerMax),
      ],
    ],
    [this.temperatureAField.key]: [
      this.temperatureAField.value,
      [
        Validators.required,
        Validators.min(this.odTemperatureMin),
        Validators.max(this.odTemperatureMax),
      ],
    ],
    [this.temperatureBField.key]: [
      this.temperatureBField.value,
      [
        Validators.required,
        Validators.min(this.odTemperatureMin),
        Validators.max(this.odTemperatureMax),
      ],
    ],
    [this.temperatureCField.key]: [
      this.temperatureCField.value,
      [
        Validators.required,
        Validators.min(this.odTemperatureMin),
        Validators.max(this.odTemperatureMax),
      ],
    ],
    [this.temperatureDField.key]: [
      this.temperatureDField.value,
      [
        Validators.required,
        Validators.min(this.odTemperatureMin),
        Validators.max(this.odTemperatureMax),
      ],
    ],
  });

  // Oxygen calibration form
  oxygenCalibrationForm = this.formBuilder.group({
    [this.temperature100AField.key]: [
      this.temperature100AField.value,
      [
        Validators.required,
        Validators.min(this.oxygenTemp100Min),
        Validators.max(this.oxygenTemp100Max),
      ],
    ],
    [this.temperature100BField.key]: [
      this.temperature100BField.value,
      [
        Validators.required,
        Validators.min(this.oxygenTemp100Min),
        Validators.max(this.oxygenTemp100Max),
      ],
    ],
    [this.temperature100CField.key]: [
      this.temperature100CField.value,
      [
        Validators.required,
        Validators.min(this.oxygenTemp100Min),
        Validators.max(this.oxygenTemp100Max),
      ],
    ],
    [this.temperature100DField.key]: [
      this.temperature100DField.value,
      [
        Validators.required,
        Validators.min(this.oxygenTemp100Min),
        Validators.max(this.oxygenTemp100Max),
      ],
    ],
    [this.temperature0AField.key]: [
      this.temperature0AField.value,
      [
        Validators.required,
        Validators.min(this.oxygenTemp0Min),
        Validators.max(this.oxygenTemp0Max),
      ],
    ],
    [this.temperature0BField.key]: [
      this.temperature0BField.value,
      [
        Validators.required,
        Validators.min(this.oxygenTemp0Min),
        Validators.max(this.oxygenTemp0Max),
      ],
    ],
    [this.temperature0CField.key]: [
      this.temperature0CField.value,
      [
        Validators.required,
        Validators.min(this.oxygenTemp0Min),
        Validators.max(this.oxygenTemp0Max),
      ],
    ],
    [this.temperature0DField.key]: [
      this.temperature0DField.value,
      [
        Validators.required,
        Validators.min(this.oxygenTemp0Min),
        Validators.max(this.oxygenTemp0Max),
      ],
    ],
    [this.dphi100AField.key]: [
      this.dphi100AField.value,
      [
        Validators.required,
        Validators.min(this.oxygenDphi100Min),
        Validators.max(this.oxygenDphi100Max),
      ],
    ],
    [this.dphi100BField.key]: [
      this.dphi100BField.value,
      [
        Validators.required,
        Validators.min(this.oxygenDphi100Min),
        Validators.max(this.oxygenDphi100Max),
      ],
    ],
    [this.dphi100CField.key]: [
      this.dphi100CField.value,
      [
        Validators.required,
        Validators.min(this.oxygenDphi100Min),
        Validators.max(this.oxygenDphi100Max),
      ],
    ],
    [this.dphi100DField.key]: [
      this.dphi100DField.value,
      [
        Validators.required,
        Validators.min(this.oxygenDphi100Min),
        Validators.max(this.oxygenDphi100Max),
      ],
    ],
    [this.dphi0AField.key]: [
      this.dphi0AField.value,
      [
        Validators.required,
        Validators.min(this.oxygenDphi0Min),
        Validators.max(this.oxygenDphi0Max),
      ],
    ],
    [this.dphi0BField.key]: [
      this.dphi0BField.value,
      [
        Validators.required,
        Validators.min(this.oxygenDphi0Min),
        Validators.max(this.oxygenDphi0Max),
      ],
    ],
    [this.dphi0CField.key]: [
      this.dphi0CField.value,
      [
        Validators.required,
        Validators.min(this.oxygenDphi0Min),
        Validators.max(this.oxygenDphi0Max),
      ],
    ],
    [this.dphi0DField.key]: [
      this.dphi0DField.value,
      [
        Validators.required,
        Validators.min(this.oxygenDphi0Min),
        Validators.max(this.oxygenDphi0Max),
      ],
    ],
    [this.pressure100AField.key]: [
      this.pressure100AField.value,
      [
        Validators.required,
        Validators.min(this.oxygenPressureMin),
        Validators.max(this.oxygenPressureMax),
      ],
    ],
    [this.pressure100BField.key]: [
      this.pressure100BField.value,
      [
        Validators.required,
        Validators.min(this.oxygenPressureMin),
        Validators.max(this.oxygenPressureMax),
      ],
    ],
    [this.pressure100CField.key]: [
      this.pressure100CField.value,
      [
        Validators.required,
        Validators.min(this.oxygenPressureMin),
        Validators.max(this.oxygenPressureMax),
      ],
    ],
    [this.pressure100DField.key]: [
      this.pressure100DField.value,
      [
        Validators.required,
        Validators.min(this.oxygenPressureMin),
        Validators.max(this.oxygenPressureMax),
      ],
    ],
  });

  phCalibrationForm = this.formBuilder.group({
    [this.phOffsetAField.key]: [
      this.phOffsetAField.value,
      [
        Validators.required,
        Validators.min(this.phOffsetMin),
        Validators.max(this.phOffsetMax),
      ],
    ],
    [this.phOffsetBField.key]: [
      this.phOffsetBField.value,
      [
        Validators.required,
        Validators.min(this.phOffsetMin),
        Validators.max(this.phOffsetMax),
      ],
    ],
    [this.phOffsetCField.key]: [
      this.phOffsetCField.value,
      [
        Validators.required,
        Validators.min(this.phOffsetMin),
        Validators.max(this.phOffsetMax),
      ],
    ],
    [this.phOffsetDField.key]: [
      this.phOffsetDField.value,
      [
        Validators.required,
        Validators.min(this.phOffsetMin),
        Validators.max(this.phOffsetMax),
      ],
    ],
    [this.phSlopeAField.key]: [
      this.phSlopeAField.value,
      [
        Validators.required,
        Validators.min(this.phSlopeMin),
        Validators.max(this.phSlopeMax),
      ],
    ],
    [this.phSlopeBField.key]: [
      this.phSlopeBField.value,
      [
        Validators.required,
        Validators.min(this.phSlopeMin),
        Validators.max(this.phSlopeMax),
      ],
    ],
    [this.phSlopeCField.key]: [
      this.phSlopeCField.value,
      [
        Validators.required,
        Validators.min(this.phSlopeMin),
        Validators.max(this.phSlopeMax),
      ],
    ],
    [this.phSlopeDField.key]: [
      this.phSlopeDField.value,
      [
        Validators.required,
        Validators.min(this.phSlopeMin),
        Validators.max(this.phSlopeMax),
      ],
    ],
  });

  spargingCalibrationForm = this.formBuilder.group({
    [this.spargingSlopeAField.key]: [
      this.spargingSlopeAField.value,
      [
        Validators.required,
        Validators.min(this.spargingSlopeMin),
        Validators.max(this.spargingSlopeMax),
      ],
    ],
    [this.spargingSlopeBField.key]: [
      this.spargingSlopeBField.value,
      [
        Validators.required,
        Validators.min(this.spargingSlopeMin),
        Validators.max(this.spargingSlopeMax),
      ],
    ],
    [this.spargingSlopeCField.key]: [
      this.spargingSlopeCField.value,
      [
        Validators.required,
        Validators.min(this.spargingSlopeMin),
        Validators.max(this.spargingSlopeMax),
      ],
    ],
    [this.spargingSlopeDField.key]: [
      this.spargingSlopeDField.value,
      [
        Validators.required,
        Validators.min(this.spargingSlopeMin),
        Validators.max(this.spargingSlopeMax),
      ],
    ],
    [this.interceptAField.key]: [
      this.interceptAField.value,
      [
        Validators.required,
        Validators.min(this.spargingInterceptMin),
        Validators.max(this.spargingInterceptMax),
      ],
    ],
    [this.interceptBField.key]: [
      this.interceptBField.value,
      [
        Validators.required,
        Validators.min(this.spargingInterceptMin),
        Validators.max(this.spargingInterceptMax),
      ],
    ],
    [this.interceptCField.key]: [
      this.interceptCField.value,
      [
        Validators.required,
        Validators.min(this.spargingInterceptMin),
        Validators.max(this.spargingInterceptMax),
      ],
    ],
    [this.interceptDField.key]: [
      this.interceptDField.value,
      [
        Validators.required,
        Validators.min(this.spargingInterceptMin),
        Validators.max(this.spargingInterceptMax),
      ],
    ],
  });

  r2Form = this.formBuilder.group({
    [this.r2AField.key]: [
      this.r2AField.value,
      [
        Validators.required,
        Validators.min(this.r2Min),
        Validators.max(this.r2Max),
      ],
    ],
    [this.r2BField.key]: [
      this.r2BField.value,
      [
        Validators.required,
        Validators.min(this.r2Min),
        Validators.max(this.r2Max),
      ],
    ],
    [this.r2CField.key]: [
      this.r2CField.value,
      [
        Validators.required,
        Validators.min(this.r2Min),
        Validators.max(this.r2Max),
      ],
    ],
    [this.r2DField.key]: [
      this.r2DField.value,
      [
        Validators.required,
        Validators.min(this.r2Min),
        Validators.max(this.r2Max),
      ],
    ],
  });

  ngOnInit(): void {
    this.authenticationService.currentUser$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user: User) => {
        this.currentUser = user;
      });

    this.route.params.subscribe((params: Params) => {
      this.calibrationType = params.calibrationType as string;
      this.calibrationId = params.id as number;
    });

    if (!this.calibrationType || !this.calibrationId) {
      this.loadingError = true;
      return;
    }

    this.laboratory = this.authenticationService.selectedLaboratory;
    this.navOptions = [
      {
        value: '100',
        labelText: '100%',
      },
      {
        value: '0',
        labelText: '0%',
      },
    ];

    // Use route data to get and set list of device ids
    if (window.history.state.data) {
      // Set calibration object from route state
      switch (this.calibrationType) {
        case 'optical-density':
          this.odCalibration = window.history.state.data as CalibrationResults;
          this.calibrationModuleType = Module.BaseModule;
          this.setCalibrationNameValidators(16);
          this.updateFormValues();
          break;
        case 'oxygen':
          this.oxygenCalibration = window.history.state
            .data as OxygenCalibration;
          this.calibrationModuleType = Module.Oxygen;

          this.setCalibrationNameValidators(10);
          this.updateFormValues();
          break;
        case 'ph':
          this.phCalibration = window.history.state.data as PhCalibration;
          this.calibrationModuleType = Module.pH;
          this.setCalibrationNameValidators(16);
          this.updateFormValues();
          break;
        case 'sparging':
          this.spargingCalibration = window.history.state
            .data as SpargingCalibration;
          this.calibrationModuleType = Module.Sparging;
          this.setCalibrationNameValidators(16);
          this.updateFormValues();
          break;
      }
    }
  }

  /**
   * @remarks
   * Convenience getter for easy access to OD value form fields
   */
  get calibrationForm(): FormGroup['controls'] {
    return this.editCalibrationForm.controls;
  }

  /**
   * @remarks
   * Convenience getter for easy access to OD value form fields
   */
  get odForm(): FormGroup['controls'] {
    return this.odCalibrationForm.controls;
  }

  /**
   * @remarks
   * Convenience getter for easy access to pH value form fields
   */
  get pHForm(): FormGroup['controls'] {
    return this.phCalibrationForm.controls;
  }

  /**
   * @remarks
   * Convenience getter for easy access to oxygen value form fields
   */
  get oxygenForm(): FormGroup['controls'] {
    return this.oxygenCalibrationForm.controls;
  }

  /**
   * @remarks
   * Convenience getter for easy access to sparging value form fields
   */
  get spargingForm(): FormGroup['controls'] {
    return this.spargingCalibrationForm.controls;
  }

  /**
   * @remarks
   * Convenience getter for easy access to r2 value form fields
   */
  get r2CalForm(): FormGroup['controls'] {
    return this.r2Form.controls;
  }

  setCalibrationNameValidators(maxLength: number): void {
    this.editCalibrationForm.controls[
      this.calibrationNameField.key
    ].setValidators([
      Validators.minLength(this.nameLengthMin),
      Validators.maxLength(maxLength),
      Validators.required,
      Validators.pattern(advancedNameRegex),
    ]);
  }

  resetForm(): void {
    this.editCalibrationForm.reset();
    this.resetCalibrationTypeForm();
    this.updateFormValues();
    this.markFormsAsPristine();
  }

  private updateFormValues(): void {
    this.displayForm = true;
    switch (this.calibrationType) {
      case 'optical-density':
        if (this.odCalibration) {
          this.editCalibrationForm.patchValue({
            calibrationName: this.odCalibration.name,
          });
          this.odCalibrationForm.patchValue(this.getOdFormValues());
          this.r2Form.patchValue(this.getOdR2FormValues());
        }
        break;
      case 'oxygen':
        if (this.oxygenCalibration) {
          this.editCalibrationForm.patchValue({
            calibrationName: this.oxygenCalibration.name,
          });
          this.oxygenCalibrationForm.patchValue(this.getOxygenFormValues());
          if (!this.oxygenCalibration.is100Percent) {
            this.disable100PercentFields();
          }
          if (!this.oxygenCalibration.isZeroPercent) {
            this.disableZeroPercentFields();
          }
        }
        break;
      case 'ph':
        if (this.phCalibration) {
          this.editCalibrationForm.patchValue({
            calibrationName: this.phCalibration.name,
          });
          this.phCalibrationForm.patchValue(this.getPhFormValues());
          this.r2Form.patchValue(this.getPhR2FormValues());
        }
        break;
      case 'sparging':
        if (this.spargingCalibration) {
          this.editCalibrationForm.patchValue({
            calibrationName: this.spargingCalibration.name,
          });
          this.spargingCalibrationForm.patchValue(this.getSpargingFormValues());
          this.r2Form.patchValue(this.getSpargingR2FormValues());
        }
        break;
    }
  }

  private disable100PercentFields(): void {
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      this.oxygenCalibrationForm
        .get(this[`temperature100${flask}Field` as const].key)
        ?.disable();
      this.oxygenCalibrationForm
        .get(this[`dphi100${flask}Field` as const].key)
        ?.disable();
      this.oxygenCalibrationForm
        .get(this[`pressure100${flask}Field` as const].key)
        ?.disable();
    });
  }

  private disableZeroPercentFields(): void {
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      this.oxygenCalibrationForm
        .get(this[`temperature0${flask}Field` as const].key)
        ?.disable();
      this.oxygenCalibrationForm
        .get(this[`dphi0${flask}Field` as const].key)
        ?.disable();
    });
  }

  private resetCalibrationTypeForm(): void {
    switch (this.calibrationType) {
      case 'optical-density':
        this.odCalibrationForm.reset();
        this.r2Form.reset();
        break;
      case 'oxygen':
        this.oxygenCalibrationForm.reset();
        break;
      case 'ph':
        this.phCalibrationForm.reset();
        this.r2Form.reset();
        break;
      case 'sparging':
        this.spargingCalibrationForm.reset();
        this.r2Form.reset();
        break;
    }
  }

  private markFormsAsPristine(): void {
    this.editCalibrationForm.markAsPristine();
    switch (this.calibrationType) {
      case 'optical-density':
        this.odCalibrationForm.markAsPristine();
        this.r2Form.markAsPristine();
        break;
      case 'oxygen':
        this.oxygenCalibrationForm.markAsPristine();
        break;
      case 'ph':
        this.phCalibrationForm.markAsPristine();
        this.r2Form.markAsPristine();
        break;
      case 'sparging':
        this.spargingCalibrationForm.markAsPristine();
        this.r2Form.markAsPristine();
        break;
    }
  }

  private getOdFormValues(): object {
    return {
      temperatureA: this.odCalibration.resultsTemperatureA,
      temperatureB: this.odCalibration.resultsTemperatureB,
      temperatureC: this.odCalibration.resultsTemperatureC,
      temperatureD: this.odCalibration.resultsTemperatureD,
      factorA: this.odCalibration.calFactor_A,
      factorB: this.odCalibration.calFactor_B,
      factorC: this.odCalibration.calFactor_C,
      factorD: this.odCalibration.calFactor_D,
      offsetA: this.odCalibration.calOffset_A,
      offsetB: this.odCalibration.calOffset_B,
      offsetC: this.odCalibration.calOffset_C,
      offsetD: this.odCalibration.calOffset_D,
      powerA: this.odCalibration.calPower_A,
      powerB: this.odCalibration.calPower_B,
      powerC: this.odCalibration.calPower_C,
      powerD: this.odCalibration.calPower_D,
    };
  }

  private getOdR2FormValues(): object {
    return {
      r2A: this.odCalibration.r2_A,
      r2B: this.odCalibration.r2_B,
      r2C: this.odCalibration.r2_C,
      r2D: this.odCalibration.r2_D,
    };
  }

  private getOxygenFormValues(): object {
    return {
      temperature100A: this.oxygenCalibration.temperature_100_A,
      temperature100B: this.oxygenCalibration.temperature_100_B,
      temperature100C: this.oxygenCalibration.temperature_100_C,
      temperature100D: this.oxygenCalibration.temperature_100_D,
      temperature0A: this.oxygenCalibration.temperature_0_A,
      temperature0B: this.oxygenCalibration.temperature_0_B,
      temperature0C: this.oxygenCalibration.temperature_0_C,
      temperature0D: this.oxygenCalibration.temperature_0_D,
      dphi100A: this.oxygenCalibration.dphi_100_A,
      dphi100B: this.oxygenCalibration.dphi_100_B,
      dphi100C: this.oxygenCalibration.dphi_100_C,
      dphi100D: this.oxygenCalibration.dphi_100_D,
      dphi0A: this.oxygenCalibration.dphi_0_A,
      dphi0B: this.oxygenCalibration.dphi_0_B,
      dphi0C: this.oxygenCalibration.dphi_0_C,
      dphi0D: this.oxygenCalibration.dphi_0_D,
      pressure100A: this.oxygenCalibration.pressure_100_A,
      pressure100B: this.oxygenCalibration.pressure_100_B,
      pressure100C: this.oxygenCalibration.pressure_100_C,
      pressure100D: this.oxygenCalibration.pressure_100_D,
    };
  }

  private getPhFormValues(): object {
    return {
      offsetA: this.phCalibration.offset_A,
      offsetB: this.phCalibration.offset_B,
      offsetC: this.phCalibration.offset_C,
      offsetD: this.phCalibration.offset_D,
      slopeA: this.phCalibration.slope_A,
      slopeB: this.phCalibration.slope_B,
      slopeC: this.phCalibration.slope_C,
      slopeD: this.phCalibration.slope_D,
    };
  }

  private getPhR2FormValues(): object {
    return {
      r2A: this.phCalibration.r2_A,
      r2B: this.phCalibration.r2_B,
      r2C: this.phCalibration.r2_C,
      r2D: this.phCalibration.r2_D,
    };
  }

  private getSpargingFormValues(): object {
    return {
      slopeA: this.spargingCalibration.slope_A,
      slopeB: this.spargingCalibration.slope_B,
      slopeC: this.spargingCalibration.slope_C,
      slopeD: this.spargingCalibration.slope_D,
      interceptA: this.spargingCalibration.intercept_A,
      interceptB: this.spargingCalibration.intercept_B,
      interceptC: this.spargingCalibration.intercept_C,
      interceptD: this.spargingCalibration.intercept_D,
    };
  }

  private getSpargingR2FormValues(): object {
    return {
      r2A: this.spargingCalibration.r2_A,
      r2B: this.spargingCalibration.r2_B,
      r2C: this.spargingCalibration.r2_C,
      r2D: this.spargingCalibration.r2_D,
    };
  }

  onSubmitUpdateCalibrationForm(): void {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    this.error = null;

    switch (this.calibrationModuleType) {
      case Module.BaseModule:
        this.odCalibrationUpdate = this.buildOdCalibrationUpdate();
        this.calibrationService
          .updateODCalibration(this.odCalibrationUpdate)
          .subscribe({
            next: (response: CalibrationResults) => {
              this.isProcessing = false;
              this.editCalibrationForm.markAsPristine();
              this.odCalibrationForm.markAsPristine();
              this.odCalibration = response;
              // Update form values with returned calibration data
              this.updateFormValues();
              // Open success modal
              this.openModal('calibration-update-success');
            },
            error: (error: ErrorObject) => {
              this.isProcessing = false;
              this.error = error;
              this.openModal('error');
            },
          });
        break;
      case Module.Oxygen:
        this.oxygenCalibrationUpdate = this.buildOxygenCalibrationUpdate();
        this.calibrationService
          .updateOxygenCalibration(this.oxygenCalibrationUpdate)
          .subscribe({
            next: (response: OxygenCalibration) => {
              this.isProcessing = false;
              this.editCalibrationForm.markAsPristine();
              this.oxygenCalibrationForm.markAsPristine();
              this.oxygenCalibration = response;
              // Update form values with returned calibration data
              this.updateFormValues();
              // Open success modal
              this.openModal('calibration-update-success');
            },
            error: (error: ErrorObject) => {
              this.isProcessing = false;
              this.error = error;
              this.openModal('error');
            },
          });
        break;
      case Module.pH:
        this.phCalibrationUpdate = this.buildPhCalibrationUpdate();
        this.calibrationService
          .updatePHCalibration(this.phCalibrationUpdate)
          .subscribe({
            next: (response: PhCalibration) => {
              this.isProcessing = false;
              this.editCalibrationForm.markAsPristine();
              this.phCalibrationForm.markAsPristine();
              this.phCalibration = response;
              // Update form values with returned calibration data
              this.updateFormValues();
              // Open success modal
              this.openModal('calibration-update-success');
            },
            error: (error: ErrorObject) => {
              this.isProcessing = false;
              this.error = error;
              this.openModal('error');
            },
          });
        break;
      case Module.Sparging:
        this.spargingCalibrationUpdate = this.buildSpargingCalibrationUpdate();
        this.calibrationService
          .updateSpargingCalibration(this.spargingCalibrationUpdate)
          .subscribe({
            next: (response: SpargingCalibration) => {
              this.isProcessing = false;
              this.editCalibrationForm.markAsPristine();
              this.spargingCalibrationForm.markAsPristine();
              this.spargingCalibration = response;
              // Update form values with returned calibration data
              this.updateFormValues();
              // Open success modal
              this.openModal('calibration-update-success');
            },
            error: (error: ErrorObject) => {
              this.isProcessing = false;
              this.error = error;
              this.openModal('error');
            },
          });
        break;
    }
  }

  private buildOdCalibrationUpdate(): CalibrationUpdate {
    return new CalibrationUpdate(
      this.calibrationForm.calibrationName.dirty
        ? this.calibrationForm.calibrationName.value
        : null,
      this.odForm.temperatureA.dirty ? this.odForm.temperatureA.value : null,
      this.odForm.temperatureB.dirty ? this.odForm.temperatureB.value : null,
      this.odForm.temperatureC.dirty ? this.odForm.temperatureC.value : null,
      this.odForm.temperatureD.dirty ? this.odForm.temperatureD.value : null,
      this.odForm.factorA.dirty ? this.odForm.factorA.value : null,
      this.odForm.factorB.dirty ? this.odForm.factorB.value : null,
      this.odForm.factorC.dirty ? this.odForm.factorC.value : null,
      this.odForm.factorD.dirty ? this.odForm.factorD.value : null,
      this.odForm.offsetA.dirty ? this.odForm.offsetA.value : null,
      this.odForm.offsetB.dirty ? this.odForm.offsetB.value : null,
      this.odForm.offsetC.dirty ? this.odForm.offsetC.value : null,
      this.odForm.offsetD.dirty ? this.odForm.offsetD.value : null,
      this.odForm.powerA.dirty ? this.odForm.powerA.value : null,
      this.odForm.powerB.dirty ? this.odForm.powerB.value : null,
      this.odForm.powerC.dirty ? this.odForm.powerC.value : null,
      this.odForm.powerD.dirty ? this.odForm.powerD.value : null,
      this.r2CalForm.r2A.dirty ? this.r2CalForm.r2A.value : null,
      this.r2CalForm.r2B.dirty ? this.r2CalForm.r2B.value : null,
      this.r2CalForm.r2C.dirty ? this.r2CalForm.r2C.value : null,
      this.r2CalForm.r2D.dirty ? this.r2CalForm.r2D.value : null,
      this.calibrationId,
      this.editCalibrationForm.controls['noteField'].dirty
        ? (this.editCalibrationForm.controls['noteField'].value as string)
        : null,
    );
  }

  private buildOxygenCalibrationUpdate(): OxygenCalibrationUpdate {
    return new OxygenCalibrationUpdate(
      this.calibrationForm.calibrationName.dirty
        ? this.calibrationForm.calibrationName.value
        : null,
      this.oxygenForm.temperature0A.dirty
        ? this.oxygenForm.temperature0A.value
        : null,
      this.oxygenForm.temperature0B.dirty
        ? this.oxygenForm.temperature0B.value
        : null,
      this.oxygenForm.temperature0C.dirty
        ? this.oxygenForm.temperature0C.value
        : null,
      this.oxygenForm.temperature0D.dirty
        ? this.oxygenForm.temperature0D.value
        : null,
      this.oxygenForm.temperature100A.dirty
        ? this.oxygenForm.temperature100A.value
        : null,
      this.oxygenForm.temperature100B.dirty
        ? this.oxygenForm.temperature100B.value
        : null,
      this.oxygenForm.temperature100C.dirty
        ? this.oxygenForm.temperature100C.value
        : null,
      this.oxygenForm.temperature100D.dirty
        ? this.oxygenForm.temperature100D.value
        : null,
      this.oxygenForm.pressure100A.dirty
        ? this.oxygenForm.pressure100A.value
        : null,
      this.oxygenForm.pressure100B.dirty
        ? this.oxygenForm.pressure100B.value
        : null,
      this.oxygenForm.pressure100C.dirty
        ? this.oxygenForm.pressure100C.value
        : null,
      this.oxygenForm.pressure100D.dirty
        ? this.oxygenForm.pressure100D.value
        : null,
      this.oxygenForm.dphi0A.dirty ? this.oxygenForm.dphi0A.value : null,
      this.oxygenForm.dphi0B.dirty ? this.oxygenForm.dphi0B.value : null,
      this.oxygenForm.dphi0C.dirty ? this.oxygenForm.dphi0C.value : null,
      this.oxygenForm.dphi0D.dirty ? this.oxygenForm.dphi0D.value : null,
      this.oxygenForm.dphi100A.dirty ? this.oxygenForm.dphi100A.value : null,
      this.oxygenForm.dphi100B.dirty ? this.oxygenForm.dphi100B.value : null,
      this.oxygenForm.dphi100C.dirty ? this.oxygenForm.dphi100C.value : null,
      this.oxygenForm.dphi100D.dirty ? this.oxygenForm.dphi100D.value : null,
      this.calibrationId,
      this.editCalibrationForm.controls['noteField'].dirty
        ? (this.editCalibrationForm.controls['noteField'].value as string)
        : null,
    );
  }

  private buildPhCalibrationUpdate(): PhCalibrationUpdate {
    return new PhCalibrationUpdate(
      this.calibrationForm.calibrationName.dirty
        ? this.calibrationForm.calibrationName.value
        : null,
      this.pHForm.offsetA.dirty ? this.pHForm.offsetA.value : null,
      this.pHForm.offsetB.dirty ? this.pHForm.offsetB.value : null,
      this.pHForm.offsetC.dirty ? this.pHForm.offsetC.value : null,
      this.pHForm.offsetD.dirty ? this.pHForm.offsetD.value : null,
      this.pHForm.slopeA.dirty ? this.pHForm.slopeA.value : null,
      this.pHForm.slopeB.dirty ? this.pHForm.slopeB.value : null,
      this.pHForm.slopeC.dirty ? this.pHForm.slopeC.value : null,
      this.pHForm.slopeD.dirty ? this.pHForm.slopeD.value : null,
      this.r2CalForm.r2A.dirty ? this.r2CalForm.r2A.value : null,
      this.r2CalForm.r2B.dirty ? this.r2CalForm.r2B.value : null,
      this.r2CalForm.r2C.dirty ? this.r2CalForm.r2C.value : null,
      this.r2CalForm.r2D.dirty ? this.r2CalForm.r2D.value : null,
      this.calibrationId,
      this.editCalibrationForm.controls['noteField'].dirty
        ? (this.editCalibrationForm.controls['noteField'].value as string)
        : null,
    );
  }

  private buildSpargingCalibrationUpdate(): SpargingCalibrationUpdate {
    return new SpargingCalibrationUpdate(
      this.calibrationForm.calibrationName.dirty
        ? this.calibrationForm.calibrationName.value
        : null,
      this.spargingForm.slopeA.dirty ? this.spargingForm.slopeA.value : null,
      this.spargingForm.slopeB.dirty ? this.spargingForm.slopeB.value : null,
      this.spargingForm.slopeC.dirty ? this.spargingForm.slopeC.value : null,
      this.spargingForm.slopeD.dirty ? this.spargingForm.slopeD.value : null,
      this.spargingForm.interceptA.dirty
        ? this.spargingForm.interceptA.value
        : null,
      this.spargingForm.interceptB.dirty
        ? this.spargingForm.interceptB.value
        : null,
      this.spargingForm.interceptC.dirty
        ? this.spargingForm.interceptC.value
        : null,
      this.spargingForm.interceptD.dirty
        ? this.spargingForm.interceptD.value
        : null,
      this.r2CalForm.r2A.dirty ? this.r2CalForm.r2A.value : null,
      this.r2CalForm.r2B.dirty ? this.r2CalForm.r2B.value : null,
      this.r2CalForm.r2C.dirty ? this.r2CalForm.r2C.value : null,
      this.r2CalForm.r2D.dirty ? this.r2CalForm.r2D.value : null,
      this.calibrationId,
      this.editCalibrationForm.controls['noteField'].dirty
        ? (this.editCalibrationForm.controls['noteField'].value as string)
        : null,
    );
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'cancel-button':
        this.allowNavigation.next(false);
        this.closeModal('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.closeModal('attention');
        break;
      case 'close-button':
        this.allowNavigation.next(false);
        this.closeModal('error');
        break;
      case 'view-calibration':
        this.closeModal('calibration-update-success');
        void this.router.navigate([
          `/calibration/${this.calibrationType}/${this.calibrationId}/calibration-data`,
        ]);
        break;
      case 'close-success-button':
        this.closeModal('calibration-update-success');
        break;
      default:
        break;
    }
  }

  closeModal(modal: string): void {
    this.modalService.close(modal);
  }

  openModal(modal: string): void {
    this.modalService.open(modal);
  }

  canExit(): Promise<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  private isNavigationAllowed(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (this.editCalibrationForm.pristine) {
        resolve(true);
      } else {
        this.openModal('attention');
        this.allowNavigation.subscribe((isConfirmed) => resolve(isConfirmed));
      }
    });
  }
}
